import React, { useState } from 'react';
import { generateSitemap, downloadSitemap } from 'utils/sitemapGenerator';
import { clearSitemapCache } from './SitemapXMLRoute';

const SitemapGenerator = () => {
  const [sitemapData, setSitemapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerateSitemap = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await generateSitemap();
      setSitemapData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSitemap = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await downloadSitemap();
      setSitemapData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = () => {
    clearSitemapCache();
    setSitemapData(null);
    alert('Sitemap cache cleared successfully!');
  };

  const handleCopyToClipboard = async () => {
    if (sitemapData?.xml) {
      try {
        await navigator.clipboard.writeText(sitemapData.xml);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = sitemapData.xml;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    },
    header: {
      marginBottom: '30px',
      textAlign: 'center'
    },
    title: {
      color: '#333',
      marginBottom: '10px'
    },
    subtitle: {
      color: '#666',
      fontSize: '16px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      transition: 'background-color 0.3s'
    },
    primaryButton: {
      backgroundColor: 'var(--primary-color)',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#dc3545',
      color: 'white'
    },
    successButton: {
      backgroundColor: '#28a745',
      color: 'white'
    },
    disabledButton: {
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      cursor: 'not-allowed'
    },
    statsCard: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '5px',
      padding: '15px',
      marginBottom: '20px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px'
    },
    statItem: {
      textAlign: 'center'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: 'var(--primary-color)',
      margin: '5px 0'
    },
    statLabel: {
      fontSize: '14px',
      color: '#666'
    },
    xmlPreview: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '5px',
      padding: '15px',
      maxHeight: '400px',
      overflow: 'auto',
      fontFamily: 'monospace',
      fontSize: '12px',
      lineHeight: '1.4'
    },
    error: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f5c6cb',
      borderRadius: '5px',
      padding: '15px',
      marginBottom: '20px'
    },
    instructions: {
      backgroundColor: '#d1ecf1',
      color: '#0c5460',
      border: '1px solid #bee5eb',
      borderRadius: '5px',
      padding: '15px',
      marginBottom: '20px'
    },
    instructionsList: {
      margin: '10px 0',
      paddingLeft: '20px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Sitemap Generator</h1>
        <p style={styles.subtitle}>
          Generate and manage your website's sitemap.xml file
        </p>
      </div>

      <div style={styles.instructions}>
        <h3>Instructions:</h3>
        <ul style={styles.instructionsList}>
          <li><strong>Generate & Preview:</strong> Creates sitemap and shows preview below</li>
          <li><strong>Download XML:</strong> Generates and downloads sitemap.xml file</li>
          <li><strong>Clear Cache:</strong> Forces regeneration on next request (cache lasts 1 hour)</li>
          <li><strong>Copy to Clipboard:</strong> Copies the XML content for manual use</li>
          <li><strong>Live URL:</strong> <code>/sitemap.xml</code> serves the cached version</li>
        </ul>
      </div>

      <div style={styles.buttonGroup}>
        <button
          style={{
            ...styles.button,
            ...(loading ? styles.disabledButton : styles.primaryButton)
          }}
          onClick={handleGenerateSitemap}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate & Preview Sitemap'}
        </button>

        <button
          style={{
            ...styles.button,
            ...(loading ? styles.disabledButton : styles.successButton)
          }}
          onClick={handleDownloadSitemap}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Download sitemap.xml'}
        </button>

        <button
          style={{
            ...styles.button,
            ...styles.secondaryButton
          }}
          onClick={handleClearCache}
        >
          Clear Cache
        </button>

        {sitemapData && (
          <button
            style={{
              ...styles.button,
              ...(copied ? styles.successButton : styles.secondaryButton)
            }}
            onClick={handleCopyToClipboard}
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {sitemapData && (
        <>
          <div style={styles.statsCard}>
            <h3>Sitemap Statistics</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{sitemapData.totalUrls}</div>
                <div style={styles.statLabel}>Total URLs</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{sitemapData.staticUrls}</div>
                <div style={styles.statLabel}>Static URLs</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{sitemapData.dynamicUrls}</div>
                <div style={styles.statLabel}>Dynamic URLs</div>
              </div>
              <div style={styles.statItem}>
                <div style={{
                  ...styles.statValue,
                  color: sitemapData.success ? '#28a745' : '#dc3545'
                }}>
                  {sitemapData.success ? 'SUCCESS' : 'PARTIAL'}
                </div>
                <div style={styles.statLabel}>Generation Status</div>
              </div>
            </div>
            <p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
              Generated: {new Date(sitemapData.generatedAt).toLocaleString()}
            </p>
            {sitemapData.error && (
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#dc3545' }}>
                Warning: {sitemapData.error}
              </p>
            )}
          </div>

          <div>
            <h3>XML Preview:</h3>
            <div style={styles.xmlPreview}>
              <pre>{sitemapData.xml}</pre>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SitemapGenerator;